import {Button, Flashbar, Header, Link} from '@amzn/awsui-components-react';
import {GrPersonalComputer} from 'react-icons/gr';
import {BsFillPersonFill} from 'react-icons/bs';
import Box from '@amzn/awsui-components-react/polaris/box';
import ExpandableSection from '@amzn/awsui-components-react/polaris/expandable-section';
import Grid from '@amzn/awsui-components-react/polaris/grid';
import React, {useEffect, useState} from 'react';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import SyntaxHighlighter from 'react-syntax-highlighter';
import {WorkflowExecutionDetails, WorkflowExecutionStatus} from '../../backend/model';
import {
    WorkflowHistoryRequesterLink,
    WorkflowHistoryStatusLink,
    WorkflowHistoryWorkflowNameLink,
} from '../TableItemLink';
import {prettifyDateLocal, prettifyDateUTC} from './date';
import {startAmosWorkflow} from '../../backend/backend';
import {parseErrorMessage} from '../../utils/errors';
import {getWorkflowHistoryPageUrl} from '../../backend/internalUrls';
import {getWorkflows} from './workflows';
import { prettifyJSON } from '../../utils/jsonHelper';

interface WorkflowHistorySummaryPanelProps extends RouteComponentProps {
    workflowExecution: WorkflowExecutionDetails;
}

const USER_ID = 'UserId';

const WorkflowHistoryDetailsPanel: React.FC<WorkflowHistorySummaryPanelProps> = (props) => {
    const [rerunButtonLoading, setRerunButtonLoading] = useState<boolean>(false);
    const [apiErrorMessage, setApiErrorMessage] = useState<string>('');
    const [rerunExecutionId, setRerunExecutionId] = useState<string>('');
    const [workflowNames, setWorkflowNames] = useState<Set<string>>(new Set<string>());

    useEffect(() => {
            getWorkflows(false).then(workflows => {
                setWorkflowNames(new Set<string>(workflows.map((workflow) => workflow.value)));
            });
        }, []);

    async function rerunWorkflow() {
        setRerunExecutionId('');
        setApiErrorMessage('');
        setRerunButtonLoading(true);
        try {
            const response = (await startAmosWorkflow(
                    props.workflowExecution.workflowName,
                    props.workflowExecution.input,
                    props.workflowExecution.region!,
                )
            ).data;
            if (response.success) {
                setRerunExecutionId(response.executionId);
            } else {
                setApiErrorMessage(response.errorMessage);
            }
        } catch (err: any) {
            setApiErrorMessage(parseErrorMessage(err));
        } finally {
            setRerunButtonLoading(false);
        }
    }

    const userAuthorized = workflowNames.has(props.workflowExecution.workflowName);

    const successMessage = (
           <span>Your workflow has started. (ID: <Link color='inverted' href={getWorkflowHistoryPageUrl(rerunExecutionId)}>
                    {rerunExecutionId}
                </Link>)
           </span>
    );

    const summary = (
        <>
            <Grid
                disableGutters
                gridDefinition={[
                    {colspan: {default: 6, xxs: 2}},
                    {colspan: {default: 6, xxs: 4}},
                    {colspan: {default: 6, xxs: 2}},
                    {colspan: {default: 6, xxs: 4}},
                    {colspan: {default: 6, xxs: 2}},
                    {colspan: {default: 6, xxs: 4}},
                    {colspan: {default: 6, xxs: 2}},
                    {colspan: {default: 6, xxs: 4}},
                    {colspan: {default: 6, xxs: 2}},
                    {colspan: {default: 6, xxs: 4}},
                    {colspan: {default: 6, xxs: 2}},
                    {colspan: {default: 6, xxs: 4}},
                    {colspan: {default: 6, xxs: 2}},
                    {colspan: {default: 6, xxs: 4}},
                    {colspan: {default: 6, xxs: 2}},
                    {colspan: {default: 6, xxs: 4}},
                    {colspan: {default: 6, xxs: 2}},
                    {colspan: {default: 6, xxs: 4}},
                    {colspan: {default: 6, xxs: 2}},
                    {colspan: {default: 6, xxs: 4}},
                    {colspan: {default: 6, xxs: 2}},
                    {colspan: {default: 6, xxs: 4}},
                    {colspan: {default: 6, xxs: 2}},
                    {colspan: {default: 6, xxs: 4}},
                    {colspan: {default: 12, xxs: 2}},
                    {colspan: {default: 12, xxs: 10}},
                    {colspan: {default: 12, xxs: 2}},
                    {colspan: {default: 12, xxs: 10}},
                ]}>
                <Box variant='p'>Execution ID:</Box>
                <Box variant='p' id='whdp-executionId'>{props.workflowExecution.executionId}</Box>
                <Box variant='p'>Workflow Name: </Box>
                <Box variant='p' id='whdp-workflowName'>
                    <WorkflowHistoryWorkflowNameLink workflowName={props.workflowExecution.workflowName}/>
                </Box>

                <Box variant='p'>Status: </Box>
                <Box id='whdp-status'>
                    <WorkflowHistoryStatusLink status={props.workflowExecution.status}/>
                </Box>
                <Box variant='p'>Region: </Box>
                <Box variant='p' id='whdp-region'>{props.workflowExecution.region ?? ''}</Box>

                <Box variant='p'>Application Name: </Box>
                <Box variant='p' id='whdp-applicationName'>{props.workflowExecution.requester.applicationName}</Box>

                <Box variant='p'>Actor Details: </Box>
                <Box variant='p' id='whdp-actorDetails'>
                    {props.workflowExecution.requester.actorType === USER_ID ?
                        <BsFillPersonFill className={'icon'} id='whdp-BsFillPersonFill'/> :
                        <GrPersonalComputer className={'icon'} id='whdp-GrPersonalComputer'/>}
                    <span style={{marginLeft: '5px'}}/>
                    <WorkflowHistoryRequesterLink requester={props.workflowExecution.requester.actorId}/>
                </Box>

                <Box variant='p'>Started At: </Box>
                <Box variant='p' id='whdp-createTime-utc'>
                    {prettifyDateUTC(new Date(props.workflowExecution.createTime))}
                </Box>
                <Box variant='p'>Last Modified At: </Box>
                <Box variant='p' id='whdp-lastModifiedTime-utc'>
                    {prettifyDateUTC(new Date(props.workflowExecution.lastModifiedTime))}
                </Box>

                <Box></Box>
                <Box variant='p' id='whdp-createTime-local'>
                    {prettifyDateLocal(new Date(props.workflowExecution.createTime))}
                </Box>
                <Box></Box>
                <Box variant='p' id='whdp-lastModifiedTime-local'>
                    {prettifyDateLocal(new Date(props.workflowExecution.lastModifiedTime))}
                </Box>

                <Box variant='p'>Error Code: </Box>
                <Box variant='p' id='whdp-errorCode'>{props.workflowExecution.errorCode}</Box>
                <Box variant='p'>Error Message: </Box>
                <Box variant='p' id='whdp-errorMessage'>{props.workflowExecution.errorMessage}</Box>

                <Box variant='p'>Input: </Box>
                <SyntaxHighlighter id='whdp-input' customStyle={{background: 'transparent'}} language="json">
                    {prettifyJSON(props.workflowExecution.input)}
                </SyntaxHighlighter>

                <Box variant='p'>Output: </Box>
                {props.workflowExecution.output ?
                    <SyntaxHighlighter id='whdp-output' customStyle={{background: 'transparent'}} language="json">
                        {prettifyJSON(props.workflowExecution.output)}
                    </SyntaxHighlighter> :
                    <Box variant='p' id='whdp-output'>{props.workflowExecution.output}</Box>}

            </Grid>
            {userAuthorized && props.workflowExecution.status === WorkflowExecutionStatus.FAILED &&
                <Button
                    className={'rerunButton'}
                    id='rerunWorkflowButton'
                    variant='primary'
                    onClick={rerunWorkflow}
                    loading={rerunButtonLoading}>
                    Rerun
                </Button>}
        </>
    );

    return (
        <div>
            <ExpandableSection
                id='workflowHistoryDetailsPanel'
                variant='container'
                header={<Header variant='h2'>Details</Header>}
                defaultExpanded>
                {summary}
            </ExpandableSection>
            {
                rerunExecutionId &&
                <Flashbar
                    id='rerunWorkflow-flashbar-success'
                    items={[
                        {
                            type: 'success',
                            content: successMessage,
                            dismissible: true,
                            onDismiss: () => setRerunExecutionId(''),
                        },
                    ]}/>
            }
            {
                apiErrorMessage &&
                <Flashbar
                    id='rerunWorkflow-flashbar-error'
                    items={[
                        {
                            type: 'error',
                            content: apiErrorMessage,
                            dismissible: true,
                            onDismiss: () => setApiErrorMessage(''),
                        },
                    ]}
                />
            }
        </div>
    );
};

export default withRouter(WorkflowHistoryDetailsPanel);
